<template>
  <div class="new-inventory-item-container">
    <BrucdcumHeader :breadcrumb="['Inventory', 'New Inventory Item']" />
    <form class="new-inventory-item-form">
      <!-- Hidden input for companyId to make the relation explicit -->
      <input type="hidden" :value="companyId" name="companyId" />
      
      <TabNavigation
        v-model="activeTab"
        :tabs="tabs"
        @update:modelValue="handleTabChange"
      />

      <!-- Tab Content -->
      <div class="new-inventory-item-tab-content">
        <!-- Basic Information Tab -->
        <div v-if="activeTab === 'basic'" class="new-inventory-item-tab-panel">
          <div class="new-inventory-item-form-col new-inventory-item-stunning-form-col">
            <!-- Row 1: Inventory Name, Items Group -->
            <div class="new-inventory-item-form-group-row">
              <div class="new-inventory-item-form-group new-inventory-item-form-group--label-input">
                <LabelInputField
                  input-id="nii-inventory-name"
                  label="Inventory Name"
                  v-model="form.inventoryName"
                  placeholder="Inventory Name"
                  flex
                  required
                  :has-error="!!validationErrors.inventoryName"
                  @input="clearFieldError('inventoryName')"
                >
                  <div class="new-inventory-item-input-helper-text">E.g. Widget A, Office Chair, USB Cable</div>
                  <div v-if="validationErrors.inventoryName" class="new-inventory-item-error-text">{{ validationErrors.inventoryName }}</div>
                </LabelInputField>
              </div>
              <div class="new-inventory-item-form-group new-inventory-item-form-group--label-dropdown">
                <LabelDropdown
                  label="Category"
                  select-id="nii-items-group"
                  v-model="form.itemGroupId"
                  flex
                  wide
                  searchable
                  menu-class="new-inventory-item-dropdown-menu"
                  :options="itemGroupDropdownOptions"
                  @change="handleItemsGroupChange"
                >
                  <div class="new-inventory-item-input-helper-text">e.g. Electronics, Furniture, Hardware</div>
                </LabelDropdown>
              </div>
            </div>
            
            <!-- Row 2: Subitem of, Commission item, Active -->
            <div class="new-inventory-item-form-group-row">
              <div
                class="new-inventory-item-form-group new-inventory-item-form-group--checkbox"
                @click="handleSubitemOfClick"
              >
                <LabelCheckboxField
                  input-id="nii-subitem-of"
                  label="Subitem of"
                  v-model="form.subitemOf"
                  :disabled="!canCreateSubitem"
                  flex
                  @change="onSubitemOfChange"
                />
              </div>
              <div class="new-inventory-item-form-group new-inventory-item-form-group--checkbox">
                <LabelCheckboxField
                  input-id="nii-commission-item"
                  label="Commission item"
                  v-model="form.commissionItem"
                  flex
                />
              </div>
              <div class="new-inventory-item-form-group new-inventory-item-form-group--checkbox">
                <LabelCheckboxField
                  input-id="nii-active"
                  label="Active"
                  v-model="form.active"
                  flex
                />
              </div>
            </div>
            
            <!-- Row 3: Parent Item Selection (only show when Subitem of is checked) -->
            <div v-if="form.subitemOf" class="new-inventory-item-form-group-row">
              <div class="new-inventory-item-form-group new-inventory-item-form-group--label-dropdown">
                <LabelDropdown
                  label="Parent Item"
                  select-id="nii-parent-item"
                  v-model="form.parentItemId"
                  flex
                  wide
                  required
                  searchable
                  menu-class="new-inventory-item-dropdown-menu"
                  :options="parentItemDropdownOptions"
                  :has-error="!!validationErrors.parentItemId"
                  @change="handleParentItemChange"
                >
                  <div v-if="validationErrors.parentItemId" class="new-inventory-item-error-text">{{ validationErrors.parentItemId }}</div>
                  <div class="new-inventory-item-input-helper-text">Select the parent item this is a subitem of</div>
                </LabelDropdown>
              </div>
            </div>
          </div>
        </div>

        <!-- Sales & Purchase Details Tab -->
        <div v-if="activeTab === 'details'" class="new-inventory-item-tab-panel">
          <div class="new-inventory-item-details-config">
            <!-- Left Column: Sales Information -->
            <div class="new-inventory-item-details-column">
              <h4 class="new-inventory-item-details-header">Sales Information</h4>
              
              <!-- I sell this product checkbox -->
              <div class="new-inventory-item-form-group new-inventory-item-form-group--checkbox">
                <LabelCheckboxField
                  input-id="nii-sell-product"
                  label="I sell this product"
                  v-model="form.sellProduct"
                  flex
                  @change="handleSellProductChange"
                />
              </div>
              
              <!-- Sales Configuration - Only show if sell product is checked -->
              <div v-if="form.sellProduct">
                <!-- Row 1: Sales Price, Income Account -->
                <div class="new-inventory-item-form-group-row">
                  <div class="new-inventory-item-form-group new-inventory-item-form-group--label-input">
                    <LabelInputField
                      input-id="nii-sales-price"
                      label="Sales Price(USD)"
                      v-model="form.salesPrice"
                      placeholder="0.00"
                      flex
                      :required="form.sellProduct"
                      :disabled="!form.sellProduct"
                      :has-error="!!validationErrors.salesPrice"
                      @input="clearFieldError('salesPrice')"
                      @blur="formatNumericInput('salesPrice')"
                    >
                      <div v-if="validationErrors.salesPrice" class="new-inventory-item-error-text">{{ validationErrors.salesPrice }}</div>
                    </LabelInputField>
                  </div>
                  <div class="new-inventory-item-form-group new-inventory-item-form-group--label-dropdown">
                    <LabelDropdown
                      label="Income Account"
                      select-id="nii-income-account"
                      v-model="form.incomeAccountId"
                      flex
                      wide
                      searchable
                      :required="form.sellProduct"
                      :disabled="!form.sellProduct"
                      menu-class="new-inventory-item-dropdown-menu"
                      :options="incomeAccountDropdownOptions"
                      :has-error="!!validationErrors.incomeAccountId"
                      @change="handleIncomeAccountChange"
                    >
                      <div v-if="validationErrors.incomeAccountId" class="new-inventory-item-error-text">{{ validationErrors.incomeAccountId }}</div>
                    </LabelDropdown>
                  </div>
                </div>
                
                <!-- Row 2: Standard Cost (when selling) -->
                <div class="new-inventory-item-form-group-row">
                  <div class="new-inventory-item-form-group new-inventory-item-form-group--label-input">
                    <LabelInputField
                      input-id="nii-standard-cost"
                      label="Standard Cost(USD)"
                      v-model="form.standardCost"
                      placeholder="0.00"
                      flex
                      :has-error="!!validationErrors.standardCost"
                      @input="onStandardCostInput"
                      @blur="formatNumericInput('standardCost')"
                    >
                      <div v-if="validationErrors.standardCost" class="new-inventory-item-error-text">{{ validationErrors.standardCost }}</div>
                    </LabelInputField>
                  </div>
                </div>
                
                <!-- Row 3: Description (full width) -->
                <div class="new-inventory-item-form-group new-inventory-item-form-group--label-description">
                  <LabelDescriptionField
                    input-id="nii-sales-description"
                    label="Description on Sales Transaction"
                    v-model="form.salesDescription"
                    :rows="2"
                    flex
                    :disabled="!form.sellProduct"
                  />
                </div>
              </div>
            </div>

            <!-- Right Column: Purchase Information -->
            <div class="new-inventory-item-details-column">
              <h4 class="new-inventory-item-details-header">Purchase Information</h4>
              
              <!-- Row 1: Purchase Price, Cost of Good Sold -->
              <div class="new-inventory-item-form-group-row">
                <div class="new-inventory-item-form-group new-inventory-item-form-group--label-input">
                  <LabelInputField
                    input-id="nii-purchase-price"
                    label="Purchase Price(USD)"
                    v-model="form.purchasePrice"
                    placeholder="0.00"
                    flex
                    :has-error="!!validationErrors.purchasePrice"
                    @input="clearFieldError('purchasePrice')"
                    @blur="formatNumericInput('purchasePrice')"
                  >
                    <div v-if="validationErrors.purchasePrice" class="new-inventory-item-error-text">{{ validationErrors.purchasePrice }}</div>
                  </LabelInputField>
                </div>
                <div class="new-inventory-item-form-group new-inventory-item-form-group--label-dropdown">
                  <LabelDropdown
                    label="Cost of Good Sold"
                    select-id="nii-cogs-account"
                    v-model="form.costOfGoodsSoldId"
                    flex
                    wide
                    required
                    searchable
                    menu-class="new-inventory-item-dropdown-menu"
                    :options="costOfGoodsSoldDropdownOptions"
                    :has-error="!!validationErrors.costOfGoodsSoldId"
                    @change="handleCostOfGoodsSoldChange"
                  >
                    <div v-if="validationErrors.costOfGoodsSoldId" class="new-inventory-item-error-text">{{ validationErrors.costOfGoodsSoldId }}</div>
                  </LabelDropdown>
                </div>
              </div>
              
              <!-- Row 2: Assets Account, Preferred Vendor -->
              <div class="new-inventory-item-form-group-row">
                <div class="new-inventory-item-form-group new-inventory-item-form-group--label-dropdown">
                  <LabelDropdown
                    label="Assets Account"
                    select-id="nii-assets-account"
                    v-model="form.assetsAccountId"
                    flex
                    wide
                    required
                    searchable
                    menu-class="new-inventory-item-dropdown-menu"
                    :options="assetsAccountDropdownOptions"
                    :has-error="!!validationErrors.assetsAccountId"
                    @change="handleAssetsAccountChange"
                  >
                    <div v-if="validationErrors.assetsAccountId" class="new-inventory-item-error-text">{{ validationErrors.assetsAccountId }}</div>
                  </LabelDropdown>
                </div>
                <div class="new-inventory-item-form-group new-inventory-item-form-group--label-dropdown">
                  <LabelDropdown
                    label="Preferred Vendor"
                    select-id="nii-vendor"
                    v-model="form.vendorId"
                    flex
                    wide
                    searchable
                    menu-class="new-inventory-item-dropdown-menu"
                    :options="vendorDropdownOptions"
                    @change="handleVendorChange"
                  />
                </div>
              </div>
              
              <!-- Row 3: Vendor Product No (single field) -->
              <div class="new-inventory-item-form-group-row">
                <div class="new-inventory-item-form-group new-inventory-item-form-group--label-input">
                  <LabelInputField
                    input-id="nii-vendor-product-no"
                    label="Vendor Product No"
                    v-model="form.vendorProductNo"
                    placeholder="Vendor Product No"
                    flex
                  />
                </div>
              </div>
              
              <!-- Row 4: Description (full width) -->
              <div class="new-inventory-item-form-group new-inventory-item-form-group--label-description">
                <LabelDescriptionField
                  input-id="nii-purchase-description"
                  label="Description on Purchase Transaction"
                  v-model="form.purchaseDescription"
                  :rows="2"
                  flex
                />
              </div>
            </div>
          </div>
          
          <!-- Inventory Information Section (Full Width) -->
          <div class="new-inventory-item-inventory-section">
            <h4 class="new-inventory-item-details-header">Inventory Information</h4>
            
            <!-- Row 1: On Hand Quantity, Total -->
            <div class="new-inventory-item-form-group-row">
              <div class="new-inventory-item-form-group new-inventory-item-form-group--label-input">
                <LabelInputField
                  input-id="nii-on-hand-qty"
                  label="On Hand Quantity"
                  v-model="form.onHandQuantity"
                  placeholder="0"
                  flex
                  @input="updateTotal"
                />
              </div>
              <div class="new-inventory-item-form-group new-inventory-item-form-group--label-input">
                <LabelInputField
                  input-id="nii-total"
                  label="Total(USD)"
                  v-model="form.total"
                  placeholder="0.00"
                  flex
                  disabled
                />
              </div>
            </div>
            
            <!-- Row 2: As of, Reorder Point -->
            <div class="new-inventory-item-form-group-row">
              <div class="new-inventory-item-form-group new-inventory-item-form-group--label-date">
                <LabelDateField
                  input-id="nii-as-of-date"
                  label="As of"
                  v-model="form.asOfDate"
                  flex
                  @change="handleAsOfDateChange"
                />
              </div>
              <div class="new-inventory-item-form-group new-inventory-item-form-group--label-input">
                <LabelInputField
                  input-id="nii-reorder-point"
                  label="Reorder Point"
                  v-model="form.reorderPoint"
                  placeholder="0"
                  flex
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="new-inventory-item-form-actions">
        <SecondaryButton label="Cancel" icon="bi bi-x-lg" @click="cancel" />
        <template v-if="activeTab === 'details'">
          <PrimaryButton label="Save and New" icon="bi bi-arrow-repeat" @click="saveAndNew" />
          <PrimaryButton label="Save and Close" icon="bi bi-check-lg" @click="saveAndClose" />
        </template>
        <PrimaryButton v-else label="Next" icon="bi bi-arrow-right" @click="handleNext" />
      </div>
    </form>
    <!-- Custom Modal for New Category -->
    <AddNewItemGroup
      :visible="showItemsGroupModal"
      :companyId="companyId"
      item-type="Inventory"
      @ok="onAddItemGroupOk"
      @cancel="closeItemsGroupModal"
    />
    
    <SuccessModal 
      :show="showSuccessModal"
      :title="successTitle"
      :message="successMessage"
      @close="closeSuccessModal"
    />
    
    <ErrorModal 
      :show="showErrorModal" 
      :title="'Error'"
      :message="errorMessage"
      @close="closeErrorModal" 
    />
  </div>
</template>

<script>
import '../styles/NewInventoryItem.css';
import '../styles/inventory-label-field-overrides.css';
import ApiService from '@/services/api.service';
import logger from '@/utils/logger';
import CompanyService from '@/services/company.service.js';
import AddNewItemGroup from '@/modules/company/views/AddNewItemGroup.vue';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import TabNavigation from '@/components/TabNavigation.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';
import SuccessModal from '@/components/SuccessModal.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import LabelInputField from '@/modules/consultant/components/LabelInputField.vue';
import LabelDateField from '@/modules/consultant/components/LabelDateField.vue';
import LabelDescriptionField from '@/modules/consultant/components/LabelDescriptionField.vue';
import LabelCheckboxField from '@/modules/consultant/components/LabelCheckboxField.vue';

export default {
  name: 'NewInventoryItem',
  components: { 
    AddNewItemGroup, 
    BrucdcumHeader,
    TabNavigation,
    PrimaryButton,
    SecondaryButton, 
    SuccessModal, 
    ErrorModal,
    LabelDropdown,
    LabelInputField,
    LabelDateField,
    LabelDescriptionField,
    LabelCheckboxField,
  },
  data() {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return {
      tabs: [
        { id: 'basic', name: 'Basic Information' },
        { id: 'details', name: 'Sales & Purchase Details' }
      ],
      activeTab: 'basic',
      form: {
        inventoryName: '',
        itemGroupId: '',
        active: true,
        subitemOf: false,
        parentItemId: '',
        commissionItem: false,
      sellProduct: true,
        salesPrice: '',
        incomeAccountId: '',
        salesDescription: '',
        purchasePrice: '',
        costOfGoodsSoldId: '',
        assetsAccountId: '',
        vendorId: '',
        standardCost: '',
        onHandQuantity: '',
        total: '',
        asOfDate: `${mm}/${dd}/${yyyy}`,
        reorderPoint: '',
        vendorProductNo: '',
        purchaseDescription: ''
      },
      itemGroups: [],
      showItemsGroupModal: false,
      newItemsGroupName: '',
      previousItemGroup: '',
      incomeAccounts: [],
      vendors: [],
      assetsAccounts: [],
      costOfGoodsSoldAccounts: [],
      inventoryItems: [],
      showSuccessModal: false,
      successTitle: 'Success',
      successMessage: '',
      showErrorModal: false,
      errorMessage: '',
      validationErrors: {},
      isSaveAndNew: false,
      isItemGroupCreated: false,
    };
  },
  computed: {
    // Make companyId available as a computed property from the route
    companyId() {
      return this.$route.params.id;
    },
    availableParentItems() {
      // Return inventory items that can be parent items (exclude current item if editing)
      return this.inventoryItems.filter(item => 
        item.active && // Only active items can be parents
        !item.subitemOf && // Items that are subitems themselves cannot be parents
        item.id !== this.form.id // Don't allow an item to be its own parent (when editing)
      );
    },
    canCreateSubitem() {
      return this.availableParentItems.length > 0;
    },
    itemGroupDropdownOptions() {
      return [
        { value: '', label: 'Select Category' },
        ...this.itemGroups.map((group) => ({
          value: group.id,
          label: group.name,
        })),
        { value: '__add_new__', label: 'Add New Category' },
      ];
    },
    parentItemDropdownOptions() {
      return [
        { value: '', label: 'Select Parent Item' },
        ...this.availableParentItems.map((item) => ({
          value: item.id,
          label: `${item.name} - ${item.itemGroup?.name || 'No Category'}`,
        })),
      ];
    },
    incomeAccountDropdownOptions() {
      return this.buildAccountDropdownOptions(this.incomeAccounts, 'Select Income Account');
    },
    costOfGoodsSoldDropdownOptions() {
      return this.buildAccountDropdownOptions(this.costOfGoodsSoldAccounts, 'Select Cost of Goods Sold Account');
    },
    assetsAccountDropdownOptions() {
      return this.buildAccountDropdownOptions(this.assetsAccounts, 'Select Assets Account');
    },
    vendorDropdownOptions() {
      const vendorList = Array.isArray(this.vendors) ? this.vendors : [];
      return [
        { value: '', label: 'Select Vendor' },
        ...vendorList.map((vendor) => ({
          value: vendor.id,
          label: vendor.vendorName || vendor.name,
        })),
        { value: '__add_new__', label: 'Add New Vendor' },
      ];
    },
  },
  created() {
    this.fetchCompanyAccounts();
    this.fetchItemGroups();
    this.fetchInventoryItems();
    this.fetchVendors();
  },
  methods: {
    handleTabChange() {
      // Handle any tab-specific logic here
    },
    
    handleSubitemOfClick() {
      if (!this.canCreateSubitem) {
        this.errorMessage = 'Cannot create subitem: No parent items available. Please create inventory items first to enable subitems.';
        this.showErrorModal = true;
      }
    },
    onSubitemOfChange(checked) {
      if (!checked) {
        this.form.parentItemId = '';
        delete this.validationErrors.parentItemId;
      }
    },

    handleSellProductChange() {
      // Clear related field errors when sell product is disabled
      if (!this.form.sellProduct) {
        delete this.validationErrors.salesPrice;
        delete this.validationErrors.incomeAccountId;
      }
    },
    
    clearFieldError(field) {
      if (this.validationErrors[field]) {
        delete this.validationErrors[field];
      }
    },
    
    async fetchCompanyAccounts() {
      try {
        const res = await ApiService.get(`/company-account/company/${this.companyId}`);
        const accounts = res.data.accounts;
        this.incomeAccounts = accounts.filter(a => a.accountType === 'Income' || a.accountType === 'Other Income');
        this.assetsAccounts = accounts.filter(a => a.accountType === 'Inventory Asset' || a.accountType === 'Fixed Asset');
        this.costOfGoodsSoldAccounts = accounts.filter(a => a.accountType === 'Cost of Goods Sold');
      } catch (error) {
        logger.error('Failed to fetch company accounts:', error);
      }
    },
    async fetchItemGroups() {
      try {
        const groups = await CompanyService.getAllItemGroups(this.companyId, 'Inventory');
        this.itemGroups = Array.isArray(groups) ? groups : [];
      } catch (err) {
        this.itemGroups = [];
      }
    },
    async fetchInventoryItems() {
      try {
        const response = await ApiService.get(`/inventory/items?companyId=${this.companyId}`);
        this.inventoryItems = Array.isArray(response.data) ? response.data : [];
      } catch (err) {
        this.inventoryItems = [];
        logger.error('Failed to fetch inventory items:', err);
      }
    },
    async fetchVendors() {
      try {
        const data = await ApiService.getAllVendors(this.companyId);
        this.vendors = Array.isArray(data) ? data : (data?.data || []);
      } catch (error) {
        this.vendors = [];
        logger.error('Failed to fetch vendors:', error);
      }
    },
    buildAccountDropdownOptions(accounts, placeholder) {
      return [
        { value: '', label: placeholder },
        { value: '__add_new__', label: 'Add New Account' },
        ...accounts.map((account) => ({
          value: account.id,
          label: `${account.accountNumber} - ${account.accountName} - ${account.accountType}`,
        })),
      ];
    },
    onStandardCostInput() {
      this.updateTotal();
      this.clearFieldError('standardCost');
    },
    handleVendorChange() {
      if (String(this.form.vendorId) === '__add_new__') {
        const companyId = this.$route.params.id;
        this.$router.push(`/company/${companyId}/dashboard/vendors/new-vendor`);
        this.form.vendorId = '';
      }
    },
    handleParentItemChange() {
      this.clearFieldError('parentItemId');
    },
    handleAsOfDateChange(date) {
      this.form.asOfDate = date;
    },
    handleIncomeAccountChange() {
      if (String(this.form.incomeAccountId) === '__add_new__') {
        this.$router.push(`/company/${this.companyId}/dashboard/new-account`);
        this.form.incomeAccountId = '';
      }
    },
    handleItemsGroupChange() {
      if (String(this.form.itemGroupId) === '__add_new__') {
        this.showItemsGroupModal = true;
        this.$nextTick(() => {
          this.form.itemGroupId = this.previousItemGroup;
        });
      } else {
        this.previousItemGroup = this.form.itemGroupId;
      }
    },
    async onAddItemGroupOk(group) {
      this.showItemsGroupModal = false;
      await this.fetchItemGroups();
      if (group && group.id) {
        this.form.itemGroupId = group.id;
        this.previousItemGroup = group.id;
        this.successTitle = 'Success';
        this.successMessage = 'Item group created successfully!';
        this.showSuccessModal = true;
        this.isItemGroupCreated = true;
      }
    },
    closeItemsGroupModal() {
      this.showItemsGroupModal = false;
    },
    handleAssetsAccountChange() {
      if (String(this.form.assetsAccountId) === '__add_new__') {
        this.$router.push(`/company/${this.companyId}/dashboard/new-account`);
        this.form.assetsAccountId = '';
      }
    },
    handleCostOfGoodsSoldChange() {
      if (String(this.form.costOfGoodsSoldId) === '__add_new__') {
        this.$router.push(`/company/${this.companyId}/dashboard/new-account`);
        this.form.costOfGoodsSoldId = '';
      }
    },
    navigateToNewAccount() {
      this.$router.push(`/company/${this.companyId}/dashboard/new-account`);
    },
    toNumberOrNull(v) {
      return v === '' ? null : Number(v);
    },
    // Validation methods
    validateSalesPrice() {
      // Clear existing error
      delete this.validationErrors.salesPrice;
      
      if (this.form.sellProduct) {
        if (!this.form.salesPrice || this.form.salesPrice === '') {
          this.validationErrors.salesPrice = 'Sales Price is required when selling this product.';
          return;
        }
        
        const salesPrice = Number(this.form.salesPrice);
        if (isNaN(salesPrice)) {
          this.validationErrors.salesPrice = 'Sales Price must be a valid number.';
          return;
        }
        
        if (salesPrice <= 0) {
          this.validationErrors.salesPrice = 'Sales Price must be greater than zero.';
          return;
        }
        
        // Check relationship with purchase price
        if (this.form.purchasePrice && !isNaN(Number(this.form.purchasePrice))) {
          const purchasePrice = Number(this.form.purchasePrice);
          if (salesPrice <= purchasePrice) {
            this.validationErrors.salesPrice = `Sales Price ($${salesPrice}) should be higher than Purchase Price ($${purchasePrice})`;
            return;
          }
        }
        
        // Check relationship with standard cost
        if (this.form.standardCost && !isNaN(Number(this.form.standardCost))) {
          const standardCost = Number(this.form.standardCost);
          if (salesPrice <= standardCost) {
            this.validationErrors.salesPrice = `Sales Price ($${salesPrice}) should be higher than Standard Cost ($${standardCost})`;
            return;
          }
        }
      }
    },
    
    validatePurchasePrice() {
      // Clear existing error
      delete this.validationErrors.purchasePrice;
      
      if (this.form.purchasePrice && this.form.purchasePrice !== '') {
        const purchasePrice = Number(this.form.purchasePrice);
        if (isNaN(purchasePrice)) {
          this.validationErrors.purchasePrice = 'Purchase Price must be a valid number.';
          return;
        }
        
        if (purchasePrice < 0) {
          this.validationErrors.purchasePrice = 'Purchase Price cannot be negative.';
          return;
        }
        
        // Check relationship with sales price
        if (this.form.sellProduct && this.form.salesPrice && !isNaN(Number(this.form.salesPrice))) {
          const salesPrice = Number(this.form.salesPrice);
          if (purchasePrice >= salesPrice) {
            this.validationErrors.purchasePrice = `Purchase Price ($${purchasePrice}) should be lower than Sales Price ($${salesPrice})`;
            return;
          }
        }
        
        // Check relationship with standard cost
        if (this.form.standardCost && !isNaN(Number(this.form.standardCost))) {
          const standardCost = Number(this.form.standardCost);
          if (purchasePrice >= standardCost) {
            this.validationErrors.purchasePrice = `Purchase Price ($${purchasePrice}) should be lower than Standard Cost ($${standardCost})`;
            return;
          }
        }
      }
    },
    
    validateStandardCost() {
      // Clear existing error
      delete this.validationErrors.standardCost;
      
      if (!this.form.standardCost || this.form.standardCost === '') {
        this.validationErrors.standardCost = 'Standard Cost is required.';
        return;
      }
      
      const standardCost = Number(this.form.standardCost);
      if (isNaN(standardCost)) {
        this.validationErrors.standardCost = 'Standard Cost must be a valid number.';
        return;
      }
      
      if (standardCost < 0) {
        this.validationErrors.standardCost = 'Standard Cost cannot be negative.';
        return;
      }
      
      if (standardCost === 0) {
        this.validationErrors.standardCost = 'Standard Cost must be greater than zero.';
        return;
      }
      
      // Check relationship with purchase price
      if (this.form.purchasePrice && !isNaN(Number(this.form.purchasePrice))) {
        const purchasePrice = Number(this.form.purchasePrice);
        if (standardCost <= purchasePrice) {
          this.validationErrors.standardCost = `Standard Cost ($${standardCost}) should be higher than Purchase Price ($${purchasePrice})`;
          return;
        }
      }
      
      // Check relationship with sales price (if selling)
      if (this.form.sellProduct && this.form.salesPrice && !isNaN(Number(this.form.salesPrice))) {
        const salesPrice = Number(this.form.salesPrice);
        if (standardCost >= salesPrice) {
          this.validationErrors.standardCost = `Standard Cost ($${standardCost}) should be lower than Sales Price ($${salesPrice})`;
          return;
        }
      }
    },
    
    // Format numeric input to ensure proper formatting
    formatNumericInput(field) {
      // Remove any non-numeric characters except decimal point
      let value = this.form[field].toString().replace(/[^\d.]/g, '');
      
      // Ensure only one decimal point
      const parts = value.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      }
      
      // Limit to 2 decimal places
      if (parts.length === 2 && parts[1].length > 2) {
        value = parts[0] + '.' + parts[1].substring(0, 2);
      }
      
      this.form[field] = value;
      
      // Validate the field after formatting
      this.validateNumericFieldOnBlur(field);
    },
    
    validateNumericFieldOnBlur(field) {
      switch(field) {
        case 'salesPrice':
      this.validateSalesPrice();
          break;
        case 'purchasePrice':
          this.validatePurchasePrice();
          break;
        case 'standardCost':
      this.validateStandardCost();
          break;
      }
      
      // Re-validate all price relationships
      this.validatePriceRelationships();
    },
    
    validatePriceRelationships() {
      // Re-validate all price fields to ensure relationships are correct
      if (this.form.salesPrice) this.validateSalesPrice();
      if (this.form.purchasePrice) this.validatePurchasePrice();
      if (this.form.standardCost) this.validateStandardCost();
    },
    
    validateCurrentTab() {
      const errors = {};
      
      switch(this.activeTab) {
        case 'basic':
          if (!this.form.inventoryName || this.form.inventoryName.trim() === '') {
            errors.inventoryName = 'Inventory Name is required.';
          }
          if (this.form.subitemOf && !this.form.parentItemId) {
            errors.parentItemId = 'Parent Item is required when this is a subitem.';
          }
          break;
          
        case 'details':
          // Sales validation
          if (this.form.sellProduct) {
            if (!this.form.salesPrice || this.form.salesPrice === '') {
              errors.salesPrice = 'Sales Price is required when selling this product.';
            }
            if (!this.form.incomeAccountId) {
              errors.incomeAccountId = 'Income Account is required when selling this product.';
            }
          }
          
          // Purchase & Inventory validation
          if (!this.form.assetsAccountId) {
            errors.assetsAccountId = 'Assets Account is required.';
          }
          if (!this.form.costOfGoodsSoldId) {
            errors.costOfGoodsSoldId = 'Cost of Goods Sold Account is required.';
          }
          break;
      }
      
      return errors;
    },
    
    handleNext() {
      // Validate current tab first
      const currentTabErrors = this.validateCurrentTab();
      
      if (Object.keys(currentTabErrors).length > 0) {
        this.validationErrors = currentTabErrors;
        const errorMessages = Object.values(currentTabErrors)
          .map(message => `• ${message}`)
          .join('\n');
        this.errorMessage = 'Please fix the following issues:\n\n' + errorMessages;
        this.showErrorModal = true;
        return;
      }

      // If no errors, move to next tab
      const tabs = ['basic', 'details'];
      const currentIndex = tabs.indexOf(this.activeTab);
      if (currentIndex < tabs.length - 1) {
        this.activeTab = tabs[currentIndex + 1];
      }
    },
    
    cancel() {
      this.$router.back();
    },
    
    async saveAndClose() {
      if (!this.validateForm()) {
        return;
      }
      
      try {
        const payload = this.buildPayload();
        await ApiService.post('/inventory/items', payload);
        this.successTitle = 'Success';
        this.successMessage = 'Inventory item saved successfully! Click OK to go back to the previous page.';
        this.showSuccessModal = true;
        this.isSaveAndNew = false;
      } catch (error) {
        this.errorMessage = error.response?.data?.error || error.message || 'Failed to save inventory item.';
        this.showErrorModal = true;
      }
    },
    
    async saveAndNew() {
      if (!this.validateForm()) {
        return;
      }
      
      try {
        const payload = this.buildPayload();
        await ApiService.post('/inventory/items', payload);
        this.successTitle = 'Success';
        this.successMessage = 'Inventory item saved successfully! Click OK to create another item.';
        this.showSuccessModal = true;
        this.isSaveAndNew = true;
      } catch (error) {
        this.errorMessage = error.response?.data?.error || error.message || 'Failed to save inventory item.';
        this.showErrorModal = true;
      }
    },
    
    validateForm() {
      // Validate all tabs
      let allErrors = {};
      
      ['basic', 'details'].forEach(tab => {
        const currentTab = this.activeTab;
        this.activeTab = tab;
        const tabErrors = this.validateCurrentTab();
        allErrors = { ...allErrors, ...tabErrors };
        this.activeTab = currentTab;
      });
      
      this.validationErrors = allErrors;
      
      if (Object.keys(allErrors).length > 0) {
        const errorMessages = Object.values(allErrors)
          .map(message => `• ${message}`)
          .join('\n');
        this.errorMessage = 'Please fix the following issues:\n\n' + errorMessages;
        this.showErrorModal = true;
        return false;
      }
      
      return true;
    },
    
    buildPayload() {
      return {
        name: this.form.inventoryName,
        descriptionSales: this.form.salesDescription,
        salesPrice: this.toNumberOrNull(this.form.salesPrice),
        incomeAccount: this.form.incomeAccountId || null,
        descriptionPurchase: this.form.purchaseDescription,
        purchasePrice: this.toNumberOrNull(this.form.purchasePrice),
        costOfGoodsSold: this.form.costOfGoodsSoldId || null,
        assetsAccount: this.form.assetsAccountId || null,
        standardCost: this.toNumberOrNull(this.form.standardCost),
        onHandQuantity: this.toNumberOrNull(this.form.onHandQuantity),
        total: this.toNumberOrNull(this.form.total),
        asOfDate: this.form.asOfDate,
        reorderPoint: this.toNumberOrNull(this.form.reorderPoint),
        itemGroupId: this.form.itemGroupId || null,
        active: this.form.active,
        vendorId: this.form.vendorId || null,
        vendorProductNo: this.form.vendorProductNo,
        commissionItem: this.form.commissionItem,
        subitemOf: this.form.subitemOf,
        parentItemId: this.form.parentItemId || null,
        sellProduct: this.form.sellProduct,
        companyId: this.companyId,
      };
    },
    
    closeSuccessModal() {
      this.showSuccessModal = false;
      if (this.isItemGroupCreated) {
        // Item group was created - just close modal and stay on same page
        // The newly created item group is already selected in the dropdown
        this.isItemGroupCreated = false;
      } else if (this.isSaveAndNew) {
        this.resetForm();
        this.activeTab = 'basic';
        this.isSaveAndNew = false;
      } else {
        this.$router.back();
      }
    },
    
    closeErrorModal() {
      this.showErrorModal = false;
      this.errorMessage = '';
    },
    
    resetForm() {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      
      this.form = {
        inventoryName: '',
        itemGroupId: '',
        active: true,
        subitemOf: false,
        parentItemId: '',
        commissionItem: false,
        sellProduct: true,
        salesPrice: '',
        incomeAccountId: '',
        salesDescription: '',
        purchasePrice: '',
        costOfGoodsSoldId: '',
        assetsAccountId: '',
        vendorId: '',
        standardCost: '',
        onHandQuantity: '',
        total: '',
        asOfDate: `${mm}/${dd}/${yyyy}`,
        reorderPoint: '',
        vendorProductNo: '',
        purchaseDescription: ''
      };
      this.validationErrors = {};
    },
    
    updateTotal() {
      const cost = parseFloat(this.form.standardCost);
      const qty = parseFloat(this.form.onHandQuantity);
      if (!isNaN(cost) && !isNaN(qty)) {
        this.form.total = (cost * qty).toFixed(2);
      } else {
        this.form.total = '';
      }
    },
  },
  watch: {
    'form.sellProduct'() {
      // Re-validate price relationships when sell product is toggled
      this.validatePriceRelationships();
    },
    'form.salesPrice'() {
      // Re-validate all price relationships when sales price changes
      this.validatePriceRelationships();
    },
    'form.purchasePrice'() {
      // Re-validate all price relationships when purchase price changes
      this.validatePriceRelationships();
    },
    'form.standardCost'() {
      // Re-validate all price relationships when standard cost changes
      this.validatePriceRelationships();
    }
  }
};
</script>

<style>
.input-error-text {
  color: #e53935;
  font-size: 0.97em;
  margin-top: 0.2em;
  margin-left: 0.1em;
}
</style> 